import { dummyFeedbackData } from '../FeedbackManagementsDemo/feedback';
import { dummySalesData } from '../FeedbackManagementsDemo/sales';
import { dummyHoursData } from '../FeedbackManagementsDemo/hours';
import { AxiosWithAuth } from '../../Utilities/authenticationService';

class ReportSummarizer {
    // Prepare feedback data for analysis
    prepareFeedbackData(feedbackData) {
        const ratingCounts = {
            good: 0,
            neutral: 0,
            bad: 0
        };
        
        const comments = [];
        
        feedbackData.forEach(feedback => {
            ratingCounts[feedback.rating]++;
            comments.push(feedback.comment);
        });

        return {
            total: feedbackData.length,
            ratingCounts,
            ratingPercentages: {
                good: Math.round(ratingCounts.good / feedbackData.length * 100),
                neutral: Math.round(ratingCounts.neutral / feedbackData.length * 100),
                bad: Math.round(ratingCounts.bad / feedbackData.length * 100)
            },
            comments: comments.slice(0, 10) // Limit to first 10 comments for analysis
        };
    }

    // Prepare sales data for analysis
    prepareSalesData(salesData) {
        if (!salesData || salesData.length === 0) return null;

        const totalSales = salesData.reduce((sum, sale) => sum + sale.sales_amount, 0);
        const averageSales = totalSales / salesData.length;
        const maxSales = Math.max(...salesData.map(sale => sale.sales_amount));
        const minSales = Math.min(...salesData.map(sale => sale.sales_amount));
        
        // Calculate prediction accuracy
        const predictions = salesData.filter(sale => sale.prediction);
        const predictionAccuracy = predictions.length > 0 ? 
            predictions.reduce((sum, sale) => {
                const accuracy = Math.abs(sale.sales_amount - sale.prediction) / sale.sales_amount;
                return sum + (1 - accuracy);
            }, 0) / predictions.length : 0;

        return {
            totalSales,
            averageSales,
            maxSales,
            minSales,
            daysCount: salesData.length,
            predictionAccuracy: Math.round(predictionAccuracy * 100),
            hasPredictions: predictions.length > 0
        };
    }

    // Prepare hours data for analysis
    prepareHoursData(hoursData) {
        if (!hoursData || hoursData.length === 0) return null;

        const totalActualHours = hoursData.reduce((sum, hour) => sum + hour.actual_hours, 0);
        const totalPlannedHours = hoursData.reduce((sum, hour) => sum + hour.planned_hours, 0);
        const averageActualHours = totalActualHours / hoursData.length;
        const averagePlannedHours = totalPlannedHours / hoursData.length;
        
        // Calculate efficiency (actual vs planned)
        const efficiency = totalPlannedHours > 0 ? 
            (totalActualHours / totalPlannedHours) * 100 : 100;

        return {
            totalActualHours,
            totalPlannedHours,
            averageActualHours,
            averagePlannedHours,
            efficiency: Math.round(efficiency),
            daysCount: hoursData.length
        };
    }

    // Call the backend API to get ChatGPT summary
    async generateSummary(feedbackData, salesData, hoursData) {
        try {
            const response = await AxiosWithAuth.post('/openai/generate_monthly_summary', {
                feedback_data: feedbackData,
                sales_data: salesData,
                hours_data: hoursData
            });

            if (response.data.summary) {
                return response.data.summary;
            } else {
                throw new Error('Invalid response format from ChatGPT');
            }
        } catch (error) {
            console.error('Error generating summary:', error);
            throw error;
        }
    }

    // Generate summary using dummy data for testing
    async generateDummySummary() {
        return this.generateSummary(dummyFeedbackData, dummySalesData, dummyHoursData);
    }

    // Test method to verify the service works
    async testSummary() {
        try {
            console.log('Testing ReportSummarizer...');
            const summary = await this.generateDummySummary();
            console.log('Summary generated successfully:', summary);
            return summary;
        } catch (error) {
            console.error('Test failed:', error);
            throw error;
        }
    }

    // Generate follow-up question response with conversation history
    async generateFollowUpResponse(question, conversationHistory, feedbackData, salesData, hoursData) {
        try {
            const response = await AxiosWithAuth.post('/openai/generate_follow_up_response', {
                question: question,
                conversation_history: conversationHistory,
                feedback_data: feedbackData,
                sales_data: salesData,
                hours_data: hoursData
            });

            if (response.data.response) {
                return response.data.response;
            } else {
                throw new Error('Invalid response format from ChatGPT');
            }
        } catch (error) {
            console.error('Error generating follow-up response:', error);
            throw error;
        }
    }

    // Generate a fallback response when AI fails
    generateFallbackResponse() {
        const responses = [
            "申し訳ございませんが、現在AIサービスに接続できません。しばらく時間をおいてから再度お試しください。",
            "AIサービスが一時的に利用できません。後ほど再度お試しください。",
            "現在システムメンテナンス中のため、AI回答機能をご利用いただけません。",
            "ネットワーク接続を確認してから再度お試しください。"
        ];
        
        return responses[Math.floor(Math.random() * responses.length)];
    }

    // Generate question suggestions based on conversation history and data
    async generateQuestionSuggestions(conversationHistory, feedbackData, salesData, hoursData) {
        try {
            const response = await AxiosWithAuth.post('/openai/generate_question_suggestions', {
                conversation_history: conversationHistory,
                feedback_data: feedbackData,
                sales_data: salesData,
                hours_data: hoursData
            });

            if (response.data.suggestions) {
                return response.data.suggestions;
            } else {
                throw new Error('Invalid response format from ChatGPT');
            }
        } catch (error) {
            console.error('Error generating question suggestions:', error);
            throw error;
        }
    }

    // Generate fallback suggestions when AI fails
    generateFallbackSuggestions(conversationHistory) {
        const baseSuggestions = [
            '売上の改善点は？',
            '労働時間の効率性は？',
            'チーム評価の傾向は？',
            '来月の改善提案は？'
        ];

        // If there's conversation history, provide more contextual suggestions
        if (conversationHistory && conversationHistory.length > 0) {
            const contextualSuggestions = [
                'より詳細な分析を教えてください',
                '具体的な改善策はありますか？',
                'データの傾向について詳しく説明してください',
                '他に気になる点はありますか？'
            ];
            return contextualSuggestions;
        }

        return baseSuggestions;
    }
}

export default ReportSummarizer; 